/**
 * Ko-fi support widgets.
 * KofiFloating: fixed bottom-left button on desktop (hidden on mobile).
 *               Opens a modal with Ko-fi embed iframe.
 * KofiButton:   icon-only button for mobile topbar / footer use.
 *               Can open modal if onClick prop is provided, otherwise navigates to Ko-fi.
 * KofiModal:    Modal with preloaded Ko-fi iframe. Iframe loads after 3s idle
 *               so it's ready instantly when the user clicks.
 */

import { useEffect, useState } from 'react'
import { useModalClose } from '../hooks/useModalClose.js'
import { api } from '../api.js'

const KOFI_URL = 'https://ko-fi.com/piteball'
const KOFI_EMBED_URL = 'https://ko-fi.com/piteball?hidefeed=true&widget=true&embed=true'

// Conservative default: assume disabled until fetch confirms otherwise.
// Avoids loading external Ko-fi resources before we know the setting.
let _kofiEnabled = false
let _manuallySet = false
const _listeners = new Set()

function _notifyListeners(value) {
  _listeners.forEach((fn) => fn(value))
}

api.settings.publicSettings()
  .then((s) => { if (!_manuallySet) _kofiEnabled = s.kofi_enabled ?? true })
  .catch(() => { if (!_manuallySet) _kofiEnabled = true })
  .finally(() => { if (!_manuallySet) _notifyListeners(_kofiEnabled) })

// Call this after toggling Ko-fi in admin settings to update all mounted widgets
export function refreshKofiEnabled(value) {
  _manuallySet = true
  _kofiEnabled = value
  _notifyListeners(value)
}

export function useKofiEnabled() {
  const [enabled, setEnabled] = useState(_kofiEnabled)
  useEffect(() => {
    // Sync with latest value (may have changed between render and effect)
    setEnabled(_kofiEnabled)
    const fn = (v) => setEnabled(v)
    _listeners.add(fn)
    return () => _listeners.delete(fn)
  }, [])
  return enabled
}

const KofiIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
  </svg>
)

/**
 * Ko-fi embed iframe — shared between KofiModal and inline panel expansion.
 * Caller controls sizing via className/style.
 */
export function KofiFrame({ className = '', style }) {
  return (
    <iframe
      src={KOFI_EMBED_URL}
      title="Ko-fi formulär"
      className={className}
      style={style}
      allow="payment"
    />
  )
}

/**
 * Modal with Ko-fi embed iframe.
 * The iframe is preloaded (hidden) after a 3s delay so it renders
 * instantly when the modal opens. It stays mounted once created.
 */
export function KofiModal({ open, onClose }) {
  const kofiEnabled = useKofiEnabled()
  const [ready, setReady] = useState(false)

  useModalClose(open, onClose)

  // Only preload iframe when Ko-fi is enabled
  useEffect(() => {
    if (!kofiEnabled) return
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setReady(true), { timeout: 5000 })
      return () => cancelIdleCallback(id)
    }
    const id = setTimeout(() => setReady(true), 3000)
    return () => clearTimeout(id)
  }, [kofiEnabled])

  // Don't render anything when disabled
  if (!kofiEnabled) return null

  const showIframe = ready || open

  return (
    <div
      className={`kofi-modal-backdrop ${open ? 'kofi-modal-open' : ''}`}
      style={{ display: showIframe ? undefined : 'none', opacity: open ? undefined : 0, pointerEvents: open ? undefined : 'none' }}
      onClick={onClose}
    >
      <div className="kofi-modal" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="kofi-modal-close"
          aria-label="Stäng"
          tabIndex={open ? 0 : -1}
        >
          ✕
        </button>
        {showIframe && <KofiFrame className="kofi-modal-iframe" />}
      </div>
    </div>
  )
}

/**
 * Fixed bottom-left floating button — desktop only (hidden <md).
 * Opens a modal with Ko-fi embed iframe.
 */
export function KofiFloating() {
  const kofiEnabled = useKofiEnabled()
  const [modalOpen, setModalOpen] = useState(false)

  if (!kofiEnabled) return null

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        aria-label="Stöd projektet på Ko-fi"
        className="kofi-floating hidden md:flex"
      >
        <img
          src="https://storage.ko-fi.com/cdn/cup-border.png"
          alt=""
          className="kofi-floating-img"
        />
        <span>Stöd projektet</span>
      </button>
      <KofiModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}

/**
 * Small inline button — for mobile/PWA topbar and footer use.
 * If onClick prop is provided, calls it instead of navigating.
 */
export function KofiButton({ className = '', onClick }) {
  const kofiEnabled = useKofiEnabled()
  if (!kofiEnabled) return null

  if (onClick) {
    return (
      <button
        onClick={onClick}
        aria-label="Stöd projektet på Ko-fi"
        className={`flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 dark:bg-white/5 dark:hover:bg-white/10 ${className}`}
      >
        <KofiIcon className="h-5 w-5" />
      </button>
    )
  }

  return (
    <a
      href={KOFI_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Stöd projektet på Ko-fi"
      className={`flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 dark:bg-white/5 dark:hover:bg-white/10 ${className}`}
    >
      <KofiIcon className="h-5 w-5" />
    </a>
  )
}
